import { useState, useMemo, useEffect } from 'react';
import Header from '../../components/feature/Header';
import ViewToggle from '../../components/feature/ViewToggle';
import FolderFilter from '../../components/feature/FolderFilter';
import GameCard from '../../components/feature/GameCard';
import Footer from '../../components/feature/Footer';
import { games } from '../../mocks/games';
import { usePocket, type FolderType } from '../../hooks/usePocket';

export default function Home() {
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [view, setView] = useState<'all' | 'pocket'>('all');
  const [folderFilter, setFolderFilter] = useState('all');

  const {
    pocket,
    addToPocket,
    removeFromPocket,
    isInPocket,
    getGameFolder
  } = usePocket();

  const deviceOptions = [
    { value: 'all', label: '전체' },
    { value: 'pc', label: 'PC' },
    { value: 'mobile', label: '모바일' },
    { value: 'both', label: '둘다' }
  ];

  const filterByDevice = (gameList: typeof games) => {
    if (deviceFilter === 'all') return gameList;
    if (deviceFilter === 'both') {
      return gameList.filter(game => game.device === 'both');
    }
    return gameList.filter(
      game => game.device === deviceFilter || game.device === 'both'
    );
  };

  const games10s = useMemo(() => {
    return filterByDevice(games.filter(game => game.ttplay === '10s'));
  }, [games, deviceFilter]);

  const gamesLoading = useMemo(() => {
    return filterByDevice(games.filter(game => game.ttplay === 'loading'));
  }, [games, deviceFilter]);

  const gamesLogin = useMemo(() => {
    return filterByDevice(games.filter(game => game.ttplay === 'login'));
  }, [games, deviceFilter]);

  const pocketGames = useMemo(() => {
    const pocketGameIds = pocket.map(item => item.gameId);
    let filtered = games.filter(game => pocketGameIds.includes(game.id));

    if (folderFilter !== 'all') {
      const folderGameIds = pocket
        .filter(item => item.folder === folderFilter)
        .map(item => item.gameId);
      filtered = filtered.filter(game => folderGameIds.includes(game.id));
    }

    return filtered;
  }, [games, pocket, folderFilter]);

  const folderCounts = useMemo(() => {
    const counts: Record<string, number> = {
      '출퇴근': 0,
      '자기 전': 0,
      '멍때리기': 0,
      '두뇌': 0
    };

    pocket.forEach(item => {
      if (counts[item.folder] !== undefined) {
        counts[item.folder]++;
      }
    });

    return counts;
  }, [pocket]);

  const renderGameSection = (
    title: string, 
    icon: string, 
    gamesData: typeof games, 
    subtitle?: string
  ) => {
    if (gamesData.length === 0) return null;

    return (
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl shadow-lg">
            <span className="text-2xl">{icon}</span>
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <h2 className="text-2xl font-bold text-white">
                {title}
              </h2>
              {subtitle && (
                <span className="text-sm text-gray-500">
                  {subtitle}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-0.5">
              {gamesData.length}개의 게임
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {gamesData.map(game => (
            <GameCard
              key={game.id}
              game={game}
              isInPocket={isInPocket(game.id)}
              currentFolder={view === 'pocket' ? getGameFolder(game.id) : undefined}
              onAddToPocket={addToPocket}
              onRemoveFromPocket={removeFromPocket}
            />
          ))}
        </div>
      </section>
    );
  };

  // 디스플레이 광고 컴포넌트
  const DisplayAd = () => {
    useEffect(() => {
      try {
        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (err) {
        console.error('AdSense error:', err);
      }
    }, []);

    return (
      <div className="w-full my-6">
        <div className="bg-gray-800/30 rounded-2xl overflow-hidden min-h-[100px] flex items-center justify-center">
          <ins className="adsbygoogle"
               style={{ display: 'block', minHeight: '100px' }}
               data-ad-client="ca-pub-5902935342183681"
               data-ad-slot="5802694193"
               data-ad-format="auto"
               data-full-width-responsive="true"></ins>
        </div>
      </div>
    );
  };

  // 인피드 광고 컴포넌트
  const InFeedAd = () => {
    useEffect(() => {
      try {
        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (err) {
        console.error('AdSense error:', err);
      }
    }, []);

    return (
      <div className="w-full my-6">
        <div className="bg-gray-800/30 rounded-2xl overflow-hidden min-h-[100px] flex items-center justify-center">
          <ins className="adsbygoogle"
               style={{ display: 'block', minHeight: '100px' }}
               data-ad-format="fluid"
               data-ad-layout-key="-61+ca+8-3b+jw"
               data-ad-client="ca-pub-5902935342183681"
               data-ad-slot="8708059138"></ins>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800">
      <Header />
      
      <ViewToggle 
        view={view} 
        onChange={setView} 
        pocketCount={pocket.length}
      />

      {view === 'all' ? (
        <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-10 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">디바이스 필터</h3>
              <div className="flex flex-wrap gap-2">
                {deviceOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setDeviceFilter(option.value)}
                    className={`px-4 py-2 text-sm font-medium transition-all whitespace-nowrap cursor-pointer rounded-full ${
                      deviceFilter === option.value
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
                        : 'bg-gray-700/50 text-gray-300 border border-gray-600 hover:border-cyan-500 hover:shadow-md'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <FolderFilter
          selectedFolder={folderFilter}
          onFolderChange={setFolderFilter}
          folderCounts={folderCounts}
        />
      )}

      <div className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <main className="w-full">
          {view === 'pocket' ? (
            pocketGames.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-3xl flex items-center justify-center">
                  <span className="text-5xl">🎮</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  주머니가 비어있습니다
                </h3>
                <p className="text-gray-400">
                  게임을 주머니에 추가해보세요!
                </p>
              </div>
            ) : (
              <section>
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-white">
                    내 주머니
                    <span className="ml-3 text-lg font-normal text-gray-400">
                      ({pocketGames.length}개)
                    </span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {pocketGames.map(game => (
                    <GameCard
                      key={game.id}
                      game={game}
                      isInPocket={isInPocket(game.id)}
                      currentFolder={getGameFolder(game.id)}
                      onAddToPocket={addToPocket}
                      onRemoveFromPocket={removeFromPocket}
                    />
                  ))}
                </div>
              </section>
            )
          ) : (
            <>
              {/* 상단 디스플레이 광고 */}
              <DisplayAd />
              
              {renderGameSection('10초 뒤', '⚡', games10s)}
              
              {games10s.length > 0 && gamesLoading.length > 0 && (
                <InFeedAd />
              )}
              
              {renderGameSection('30초 뒤', '⏳', gamesLoading, '로딩 길어요')}
              
              {gamesLoading.length > 0 && gamesLogin.length > 0 && (
                <InFeedAd />
              )}
              
              {renderGameSection('2분 뒤', '🔐', gamesLogin, '로그인 필요')}
              
              {/* 하단 디스플레이 광고 */}
              {(games10s.length > 0 || gamesLoading.length > 0 || gamesLogin.length > 0) && (
                <DisplayAd />
              )}
              
              {games10s.length === 0 && gamesLoading.length === 0 && gamesLogin.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-3xl flex items-center justify-center">
                    <span className="text-5xl">🎮</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    게임을 찾을 수 없습니다
                  </h3>
                  <p className="text-gray-400">
                    다른 필터를 선택해보세요
                  </p>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}
